import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import MeetingList from './MeetingList';
import MeetingModal from './MeetingModal';
import ProposalResponsesModal from './ProposalResponsesModal';
import ProposeTimesModal from './ProposeTimesModal';
import RescheduleMeetingModal from './RescheduleMeetingModal';
import AttendeeResponseModal from './AttendeeResponseModal';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import MeetingNotesSection from './notes/MeetingNotesSection';
import { useMeetings, useMeetingConfig } from '../hooks/useMeetings';
import { getMeetingDisplayName, formatScheduledAt, MEETING_TEMPLATES } from '../utils/meetingTemplates';
import { arMittMote } from '../utils/mittMote';
import { API_URL } from '../../../utils/api';

// Lazy load protocol builder
const ProtocolBuilder = lazy(() => import('./protocol/ProtocolBuilder'));

const MeetingsPage = ({ projectId, project }) => {
  const { user } = useAuth();

  // Hooks
  const {
    meetings,
    loading,
    error,
    fetchMeetings,
    createMeeting,
    createAndPropose,
    createAndBook,
    updateMeeting,
    deleteMeeting,
    proposeTimesForMeeting,
    bookMeeting,
    rescheduleMeeting,
    completeMeeting,
    cancelMeeting
  } = useMeetings(projectId);

  const { config } = useMeetingConfig();

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [responseMeeting, setResponseMeeting] = useState(null);
  const [showAttendeeResponseModal, setShowAttendeeResponseModal] = useState(false);
  const [attendeeResponseMeeting, setAttendeeResponseMeeting] = useState(null);
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [proposeMeeting, setProposeMeeting] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleMeetingData, setRescheduleMeetingData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelMeetingData, setCancelMeetingData] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Notes panel state
  const [notesPanelMeeting, setNotesPanelMeeting] = useState(null);
  const [notesCounts, setNotesCounts] = useState({});

  // Protokollet har ingen egen vy — det öppnas på mötet. Samma state används
  // från möteskortet, från anteckningspanelen och från en notislänk.
  const [protocolMeeting, setProtocolMeeting] = useState(null);
  const [fokusMoteId, setFokusMoteId] = useState(null);

  const oppnaProtokoll = (meeting) => {
    setProtocolMeeting(meeting);
    // Släck mötets "ny uppdatering"-prick. Gjordes förut bara av Protokoll-
    // vyn; utan anropet här skulle pricken aldrig försvinna.
    if (meeting.hasUnseenProtocolUpdate && meeting.protocolId) {
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/api/meeting-protocols/${meeting.protocolId}/mark-action-updates-seen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => { /* bästa-försök — pricken står kvar till nästa gång */ });
    }
  };

  // Auto-öppna create-modalen om vi navigerats hit från Navbarens "Möte"-knapp
  // (URL: /workspace/projektering/:id/moten?createMeeting=1)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('createMeeting') === '1') {
      setShowCreateModal(true);
      // Rensa paramen så reload eller tab-byte inte triggar modalen igen
      const next = new URLSearchParams(searchParams);
      next.delete('createMeeting');
      setSearchParams(next, { replace: true });
    }
    // Garden !showCreateModal är borttagen: setShowCreateModal(true) när den
    // redan är true är en no-op, och paramen rensas i samma svep så villkoret
    // ovan är falskt vid nästa körning ändå.
  }, [searchParams, setSearchParams]);

  // ?protokoll=<mötes-id> kommer från protokollnotisen: öppna protokollet
  // direkt. Väntar tills mötena laddats, och rensar sedan paramen.
  const protokollParam = searchParams.get('protokoll');
  useEffect(() => {
    if (!protokollParam || loading) return;
    const mote = meetings.find(m => m._id === protokollParam);
    if (mote) {
      setFokusMoteId(mote._id);
      oppnaProtokoll(mote);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('protokoll');
    setSearchParams(next, { replace: true });
    // oppnaProtokoll är en ny funktion vid varje render men gör bara setState
    // och ett fristående anrop; effekten ska styras av paramen och mötena.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protokollParam, loading, meetings]);

  // Hämta anteckningsräkning för egna möten — andras möten visas bara för
  // protokollets skull och har ingen anteckningsrad.
  useEffect(() => {
    const fetchNotesCounts = async () => {
      if (!meetings || meetings.length === 0) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      const counts = {};
      await Promise.all(
        meetings
          .filter(m => (m.status === 'scheduled' || m.status === 'completed') && arMittMote(m, user))
          .map(async (meeting) => {
            try {
              const res = await fetch(`${API_URL}/api/meeting-notes/meeting/${meeting._id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (res.ok) {
                const data = await res.json();
                counts[meeting._id] = data.data?.counts?.total || 0;
              }
            } catch (err) {
              // Ignorera fel
            }
          })
      );
      setNotesCounts(counts);
    };

    fetchNotesCounts();
  }, [meetings, user]);

  // Kolla om användaren behöver svara på ett föreslaget möte
  const userNeedsToRespond = (meeting) => {
    if (meeting.status !== 'proposed') return false;
    const attendee = meeting.attendees?.find(a =>
      a.userId === user?._id ||
      a.userId?._id === user?._id ||
      a.email === user?.email
    );
    if (!attendee) return false;
    return !attendee.proposalResponses || attendee.proposalResponses.length === 0;
  };

  // Kolla om användaren kan redigera mötet
  const canEditMeeting = ['byggkonsult', 'admin'].includes(user?.role);

  // Filtrera bort mötestyper som redan har nått maxPerProject
  // OBS: Måste vara före villkorade returns för att följa React hooks-regler
  const availableTypes = useMemo(() => {
    return (config.meetingTypes || []).filter(type => {
      const template = MEETING_TEMPLATES[type.type];
      // Om maxPerProject är null/undefined = obegränsat
      if (!template?.maxPerProject) return true;

      // Räkna hur många aktiva möten av denna typ som finns (inte cancelled)
      const existingCount = meetings.filter(m =>
        m.type === type.type && m.status !== 'cancelled'
      ).length;

      return existingCount < template.maxPerProject;
    });
  }, [config.meetingTypes, meetings]);

  // Kolla om användaren kan skapa möten
  const canCreateMeeting = availableTypes.length > 0;

  // Hantera klick på möte
  const handleMeetingClick = (meeting) => {
    // Kolla om användaren är skaparen av mötet
    const isCreator = meeting.createdBy?._id === user?._id ||
      meeting.createdBy === user?._id;

    if (meeting.status === 'proposed') {
      if (['byggkonsult', 'admin'].includes(user?.role) || isCreator) {
        // Visa svarsstatus för föreslagen möten (BK/admin eller skaparen)
        setResponseMeeting(meeting);
        setShowResponsesModal(true);
      } else if (userNeedsToRespond(meeting)) {
        // Andra deltagare kan svara på mötesförslag
        setAttendeeResponseMeeting(meeting);
        setShowAttendeeResponseModal(true);
      }
      // Om redan svarat - gör ingenting (kortet är oklickbart)
    } else if ((canEditMeeting && arMittMote(meeting, user)) || isCreator) {
      // BK/admin eller skaparen kan redigera möte (t.ex. utkast)
      setSelectedMeeting(meeting);
    }
    // Andra roller på icke-proposed möten - gör ingenting (kortet är oklickbart)
  };

  // Hantera spara (skapa eller uppdatera)
  const handleSave = async (data) => {
    if (selectedMeeting) {
      await updateMeeting(selectedMeeting._id, data);
    } else {
      await createMeeting(data);
    }
  };

  // Hantera avsluta modal
  const handleCloseModal = () => {
    setSelectedMeeting(null);
    setShowCreateModal(false);
  };

  // Visa laddning
  if (loading && meetings.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Visa fel
  if (error) {
    return (
      <div className="text-center py-16">
        <div className="text-red-500 mb-2">Kunde inte ladda möten</div>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold text-gray-900">Möten</h2>
          <p className="text-sm text-gray-500 mt-1">
            Hantera projektmöten, tidsförslag och bokningar
          </p>
        </div>
        {canCreateMeeting && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-2.5 py-2 sm:px-4 text-sm font-medium text-white bg-leadme-pink rounded-lg hover:bg-leadme-pink/90 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Nytt möte</span>
          </button>
        )}
      </div>

      {/* Möteslista och anteckningspanel */}
      <div className="flex gap-6">
        {/* Möteslista */}
        <div className={`${notesPanelMeeting ? 'flex-1' : 'w-full'} transition-all`}>
          <MeetingList
            meetings={meetings}
            loading={loading}
            onMeetingClick={handleMeetingClick}
            onReschedule={(meeting) => {
              // Bokade möten: enkel ombokning med datum/tid-picker
              // Andra möten: skicka nya tidsförslag
              if (meeting.status === 'scheduled') {
                setRescheduleMeetingData(meeting);
                setShowRescheduleModal(true);
              } else {
                setProposeMeeting(meeting);
                setShowProposeModal(true);
              }
            }}
            onDelete={(meeting) => {
              setCancelMeetingData(meeting);
              setShowCancelModal(true);
            }}
            onAddNote={(meeting) => setNotesPanelMeeting(meeting)}
            onOpenProtocol={oppnaProtokoll}
            fokusMoteId={fokusMoteId}
            notesCounts={notesCounts}
          />
        </div>

        {/* Anteckningspanel */}
        {notesPanelMeeting && (
          /* z-50 på mobil: MobileBottomNav ligger fixed bottom-0 med z-40, och
             vid samma z-index vann den eftersom den kommer senare i DOM:en —
             menyn täckte anteckningsradens diktering, Delad/Privat och skicka.
             Panelen är ett fullskärmsläge med egen stäng-knapp, så den får
             ligga över navigeringen. Samma nivå som övriga mobilsheets. */
          <div className="fixed inset-0 z-50 bg-white sm:static sm:z-auto sm:w-96 flex-shrink-0 sm:bg-white sm:rounded-lg sm:border sm:shadow-sm flex flex-col sm:max-h-[calc(100vh-200px)] sm:sticky sm:top-4">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-lg">
              <div>
                <h3 className="font-medium text-gray-900">
                  {getMeetingDisplayName(notesPanelMeeting)}
                </h3>
                {notesPanelMeeting.scheduledAt && (
                  <p className="text-xs text-gray-500">{formatScheduledAt(notesPanelMeeting.scheduledAt)}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Protokoll-knapp */}
                <button
                  onClick={() => oppnaProtokoll(notesPanelMeeting)}
                  className="p-1.5 text-gray-500 hover:text-leadme-pink hover:bg-leadme-pink/10 rounded transition-colors"
                  title={notesPanelMeeting.protocolId ? 'Öppna protokoll' : 'Skapa protokoll'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
                {/* Stäng-knapp */}
                <button
                  onClick={() => setNotesPanelMeeting(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  title="Stäng"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Notes section */}
            <div className="flex-1 overflow-hidden">
              <MeetingNotesSection
                meetingId={notesPanelMeeting._id}
                meetingTitle={getMeetingDisplayName(notesPanelMeeting)}
                showHeader={false}
              />
            </div>
          </div>
        )}
      </div>

      {/* Protokollet — skapas, läses, publiceras och bockas av här */}
      {protocolMeeting && (
        <Suspense fallback={
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        }>
          <ProtocolBuilder
            meetingId={protocolMeeting._id}
            meeting={protocolMeeting}
            onClose={() => {
              setProtocolMeeting(null);
              // Uppdatera möten för protokollets status, framsteg och prick
              fetchMeetings();
            }}
          />
        </Suspense>
      )}

      {/* Skapa-modal */}
      {showCreateModal && (
        <MeetingModal
          isOpen={true}
          onClose={handleCloseModal}
          project={project}
          availableTypes={availableTypes}
          onSave={createMeeting}
          onSaveAndPropose={createAndPropose}
          onSaveAndBook={createAndBook}
          onPropose={proposeTimesForMeeting}
          onBook={bookMeeting}
        />
      )}

      {/* Redigera/visa-modal */}
      {selectedMeeting && (
        <MeetingModal
          isOpen={true}
          onClose={handleCloseModal}
          meeting={selectedMeeting}
          project={project}
          availableTypes={config.meetingTypes}
          onSave={handleSave}
          onSaveAndPropose={createAndPropose}
          onSaveAndBook={createAndBook}
          onPropose={proposeTimesForMeeting}
          onBook={bookMeeting}
          onComplete={completeMeeting}
          onCancel={cancelMeeting}
          onDelete={deleteMeeting}
        />
      )}

      {/* Svarsstatus-modal */}
      {showResponsesModal && responseMeeting && (
        <ProposalResponsesModal
          isOpen={true}
          onClose={() => {
            setShowResponsesModal(false);
            setResponseMeeting(null);
          }}
          meeting={responseMeeting}
          onBook={bookMeeting}
          onDelete={async (meetingId) => {
            await deleteMeeting(meetingId);
            fetchMeetings();
          }}
          onReschedule={(meeting) => {
            setShowResponsesModal(false);
            setResponseMeeting(null);
            // Öppna tidsförslag-modalen direkt
            setProposeMeeting(meeting);
            setShowProposeModal(true);
          }}
        />
      )}

      {/* Deltagarens svarsmodal (byggare, IA, etc.) */}
      {showAttendeeResponseModal && attendeeResponseMeeting && (
        <AttendeeResponseModal
          isOpen={true}
          onClose={() => {
            setShowAttendeeResponseModal(false);
            setAttendeeResponseMeeting(null);
          }}
          meeting={attendeeResponseMeeting}
          userId={user?._id}
          onResponseSubmitted={() => {
            setShowAttendeeResponseModal(false);
            setAttendeeResponseMeeting(null);
            fetchMeetings();
          }}
        />
      )}

      {/* Tidsförslag-modal (för ombokning av icke-bokade möten) */}
      {showProposeModal && proposeMeeting && (
        <ProposeTimesModal
          isOpen={true}
          onClose={() => {
            setShowProposeModal(false);
            setProposeMeeting(null);
          }}
          meeting={proposeMeeting}
          onPropose={async (meetingId, proposals, message) => {
            await proposeTimesForMeeting(meetingId, proposals, message);
            setShowProposeModal(false);
            setProposeMeeting(null);
            fetchMeetings();
          }}
        />
      )}

      {/* Ombokning-modal (för bokade möten - enkel datum/tid-picker) */}
      {showRescheduleModal && rescheduleMeetingData && (
        <RescheduleMeetingModal
          isOpen={true}
          onClose={() => {
            setShowRescheduleModal(false);
            setRescheduleMeetingData(null);
          }}
          meeting={rescheduleMeetingData}
          onReschedule={async (meetingId, { date, time }) => {
            await rescheduleMeeting(meetingId, { date, time });
            setShowRescheduleModal(false);
            setRescheduleMeetingData(null);
            fetchMeetings();
          }}
        />
      )}

      {/* Bekräftelsedialog för avbokning */}
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelMeetingData(null);
        }}
        onConfirm={async () => {
          if (!cancelMeetingData) return;
          try {
            setIsCancelling(true);
            await cancelMeeting(cancelMeetingData._id);
            setShowCancelModal(false);
            setCancelMeetingData(null);
            fetchMeetings();
          } catch (err) {
            console.error('Fel vid avbokning:', err);
          } finally {
            setIsCancelling(false);
          }
        }}
        title="Avboka möte"
        message={cancelMeetingData ? `Är du säker på att du vill avboka ${getMeetingDisplayName(cancelMeetingData).toLowerCase()}?` : ''}
        confirmText="Ja, avboka"
        cancelText="Avbryt"
        type="danger"
        isLoading={isCancelling}
      >
        {cancelMeetingData && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            {cancelMeetingData.scheduledAt && (
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatScheduledAt(cancelMeetingData.scheduledAt)}
              </div>
            )}
            {cancelMeetingData.location?.displayName && (
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {cancelMeetingData.location.displayName}
              </div>
            )}
            <p className="text-xs text-amber-600 mt-2">
              Kalenderinbjudan kommer att tas bort för alla deltagare.
            </p>
          </div>
        )}
      </ConfirmationModal>

    </div>
  );
};

export default MeetingsPage;
